// LSP-15050: org-level date-format tokens. The backend cannot import frontend code, so this file is
// intentionally duplicated at apps/contact-api/src/helpers/dateFormat.ts — KEEP THE TWO IN SYNC.
// MDY values reproduce the historical hard-coded formats byte-for-byte; DMY is the day-first mirror.
export type OrgDateFormat = "MDY" | "DMY";

export const DEFAULT_DATE_FORMAT: OrgDateFormat = "MDY";

export interface DateTokens {
	dateShort: string;
	dateShortPadded: string;
	scheduledDatePart: string;
	dateTimeZ: string;
	// Long-form, month-name tokens (LSP-15050 rework). MDY reproduces the historical hard-coded
	// strings byte-for-byte; DMY swaps the month/day ORDER (and drops the post-day comma where the
	// MDY form has one, e.g. "MMM D, YYYY" → "D MMM YYYY") while keeping weekday length,
	// ordinal-vs-plain day, and year presence identical to the MDY mirror.
	dateLong: string; // "MMMM D, YYYY"  → "D MMMM YYYY"
	dateLongWithWeekday: string; // "ddd, MMMM Do YYYY" → "ddd, Do MMMM YYYY"
	dateShortMonthWithWeekday: string; // "ddd, MMM D YYYY" → "ddd, D MMM YYYY"
	dateShortMonth: string; // "MMM D YYYY" → "D MMM YYYY"
	dateShortMonthCommaYear: string; // "MMM D, YYYY" → "D MMM YYYY"
	dateShortMonthOrdinal: string; // "MMM Do, YYYY" → "Do MMM YYYY"
	weekdayMonthDayShortNoYear: string; // "ddd, MMM DD" → "ddd, DD MMM"
	weekdayMonthDayOrdinalNoYear: string; // "dddd, MMM Do" → "dddd, Do MMM"
	weekdayMonthDayNoYear: string; // "dddd, MMM D" → "dddd, D MMM"
	weekdayFullMonthDayOrdinal: string; // "dddd, MMMM Do, YYYY" → "dddd, Do MMMM YYYY"
}

const TOKENS: Record<OrgDateFormat, DateTokens> = {
	MDY: {
		dateShort: "M/D/YYYY",
		dateShortPadded: "MM/DD/YYYY",
		scheduledDatePart: "M/DD/YYYY",
		dateTimeZ: "MM/DD/YYYY h:mm A z",
		dateLong: "MMMM D, YYYY",
		dateLongWithWeekday: "ddd, MMMM Do YYYY",
		dateShortMonthWithWeekday: "ddd, MMM D YYYY",
		dateShortMonth: "MMM D YYYY",
		dateShortMonthCommaYear: "MMM D, YYYY",
		dateShortMonthOrdinal: "MMM Do, YYYY",
		weekdayMonthDayShortNoYear: "ddd, MMM DD",
		weekdayMonthDayOrdinalNoYear: "dddd, MMM Do",
		weekdayMonthDayNoYear: "dddd, MMM D",
		weekdayFullMonthDayOrdinal: "dddd, MMMM Do, YYYY"
	},
	DMY: {
		dateShort: "D/M/YYYY",
		dateShortPadded: "DD/MM/YYYY",
		scheduledDatePart: "DD/M/YYYY",
		dateTimeZ: "DD/MM/YYYY h:mm A z",
		dateLong: "D MMMM YYYY",
		dateLongWithWeekday: "ddd, Do MMMM YYYY",
		dateShortMonthWithWeekday: "ddd, D MMM YYYY",
		dateShortMonth: "D MMM YYYY",
		dateShortMonthCommaYear: "D MMM YYYY",
		dateShortMonthOrdinal: "Do MMM YYYY",
		weekdayMonthDayShortNoYear: "ddd, DD MMM",
		weekdayMonthDayOrdinalNoYear: "dddd, Do MMM",
		weekdayMonthDayNoYear: "dddd, D MMM",
		weekdayFullMonthDayOrdinal: "dddd, Do MMMM YYYY"
	}
};

export function getDateTokens(format?: string | null): DateTokens {
	return TOKENS[format as OrgDateFormat] ?? TOKENS[DEFAULT_DATE_FORMAT];
}
